"use client"

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Bot, Binary, Clock, Fingerprint, KeyRound, Link2, MailSearch, Network, Route, ShieldCheck } from "lucide-react";
import { buildLocalizedPath } from "@/lib/i18n";
import { useTranslations } from "@/components/i18n/use-translations";

const toolTabs = [
    {
        key: "emailHeaderAnalyzer",
        href: "/tools/email-header-analyzer",
        icon: MailSearch,
        isNew: true,
    },
    {
        key: "urlDeconstructor",
        href: "/tools/deconstruct-url",
        icon: Link2,
    },
    {
        key: "ipInspector",
        href: "/tools/ip-inspector",
        icon: Network,
    },
    {
        key: "defang",
        href: "/tools/defang-url",
        icon: ShieldCheck,
    },
    {
        key: "iocExtractor",
        href: "/tools/ioc-extractor",
        icon: Bot,
    },
    {
        key: "base64Lab",
        href: "/tools/base64-lab",
        icon: Binary,
    },
    {
        key: "hashLab",
        href: "/tools/hash-lab",
        icon: Fingerprint,
    },
    {
        key: "jwtDecoder",
        href: "/tools/jwt-decoder",
        icon: KeyRound,
        isNew: true,
    },
    {
        key: "timestampConverter",
        href: "/tools/timestamp-converter",
        icon: Clock,
        isNew: true,
    },
    {
        key: "cidrCalculator",
        href: "/tools/cidr-calculator",
        icon: Route,
        isNew: true,
    },
];

export function ToolsTabs() {
    const pathname = usePathname();
    const { lang, messages } = useTranslations();

    return (
        <div className="w-full max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {toolTabs.map((tab) => {
                    const Icon = tab.icon;
                    const href = buildLocalizedPath(tab.href, lang);
                    const isActive = pathname === href;
                    const content = messages.tools.tabs[tab.key as keyof typeof messages.tools.tabs];

                    if (typeof content === "string") {
                        return null;
                    }

                    return (
                        <Link
                            key={tab.href}
                            href={href}
                            className={`group flex items-start gap-3 p-4 rounded-xl border transition-all duration-200 bg-slate-900/50 hover:bg-slate-900/80 hover:border-cyan-600/60 ${
                                isActive
                                    ? "border-cyan-500/80 shadow-lg shadow-cyan-500/20"
                                    : "border-slate-800/70"
                            }`}
                        >
                            <div className="mt-1 rounded-lg bg-cyan-500/10 p-2 text-cyan-400">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-base font-semibold text-white">{content.title}</span>
                                    {"isNew" in tab && tab.isNew && !isActive && (
                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-500/40">
                                            {messages.tools.tabs.new}
                                        </span>
                                    )}
                                    {isActive && (
                                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 border border-cyan-500/40">
                                            {messages.tools.tabs.active}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-400 leading-relaxed">{content.description}</p>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
